import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { deleteShopProduct, request, getMyShopProducts, toAbsUrl } from "../utils/api";
import "../styles/myProducts.css";

/* =========================================================
   Helpers
   ========================================================= */
function absUrl(u) {
  if (!u) return "";
  const s = String(u).trim();
  if (!s) return "";
  if (!/[\\/]/.test(s) && !s.startsWith("http")) return toAbsUrl(`uploads/${s}`);
  return toAbsUrl(s);
}

function pickId(p) {
  return String(p?._id || p?.id || p?.productId || p?.product?._id || p?.product?.id || "").trim();
}

function pickImage(p) {
  if (!p) return "";
  const asVal = (item) => {
    if (!item) return "";
    if (typeof item === "string") return item;
    if (typeof item === "object") return String(item.url || item.secure_url || item.path || item.image || "").trim();
    return "";
  };
  const first = (arr) => {
    if (!arr) return "";
    if (typeof arr === "string") return arr;
    if (Array.isArray(arr) && arr.length > 0) return asVal(arr[0]);
    return "";
  };

  const a = first(p.displayImages); if (a) return absUrl(a);
  const vi = Array.isArray(p?.variants) ? p.variants.flatMap((v) => Array.isArray(v?.images) ? v.images : []).filter(Boolean) : [];
  const vb = first(vi); if (vb) return absUrl(vb);
  const b = first(p.images); if (b) return absUrl(b);
  const c = first(p.media?.images); if (c) return absUrl(c);
  if (p.media?.image) return absUrl(p.media.image);
  if (p.imageUrl) return absUrl(p.imageUrl);
  if (p.image) return absUrl(p.image);
  if (p.mainImage) return absUrl(p.mainImage);
  if (p.thumbnail) return absUrl(p.thumbnail);
  return "";
}

function normStr(v) { return String(v || "").trim(); }
function normLower(v) { return normStr(v).toLowerCase(); }
function num(v, fb = 0) { const x = Number(v); return Number.isFinite(x) ? x : fb; }

function normalizeProductsResponse(data) {
  if (Array.isArray(data)) return data;
  return data?.products || data?.items || data?.data || data?.result || [];
}

function normIndustries(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.industries)) return payload.industries;
  if (payload.data && Array.isArray(payload.data.industries)) return payload.data.industries;
  return [];
}

function getIndustryKey(ind) { return String(ind?.industryId || ind?._id || ind?.id || ""); }
function getCategoryKey(cat) { return String(cat?.categoryId || cat?._id || cat?.id || ""); }

async function fetchFeaturedIdsBestEffort() {
  const urls = ["/api/shop/featured-products", "/api/shop/featured", "/api/shop/feed/featured", "/api/shop/feed/config"];
  for (const u of urls) {
    try {
      const r = await request(u, { method: "GET" });
      const ids = Array.isArray(r?.featuredProductIds) ? r.featuredProductIds.map(String).filter(Boolean)
        : Array.isArray(r?.products) ? r.products.map((p) => String(p?._id || p?.id || p)).filter(Boolean)
        : Array.isArray(r?.featuredProducts) ? r.featuredProducts.map((p) => String(p?._id || p?.id || p)).filter(Boolean)
        : null;
      if (ids) return ids;
    } catch {}
  }
  return [];
}

async function saveFeaturedIdsBestEffort(ids) {
  const urls = ["/api/shop/featured-products", "/api/shop/featured"];
  for (const u of urls) {
    try {
      await request(u, { method: "PUT", body: JSON.stringify({ featuredProductIds: ids }), headers: { "Content-Type": "application/json" } });
      return;
    } catch {}
  }
}

/* =========================================================
   MAIN COMPONENT
   ========================================================= */
export default function MyProducts() {
  const nav = useNavigate();

  // Data
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [featuredIds, setFeaturedIds] = useState([]);
  const [savingFeatured, setSavingFeatured] = useState(false);

  // Filters
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState(""); // "" | "in_stock" | "out_of_stock" | "featured"
  const [industryId, setIndustryId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [sort, setSort] = useState("newest");

  // Industries/categories
  const [industries, setIndustries] = useState([]);
  const [categories, setCategories] = useState([]);

  // View mode
  const [viewMode, setViewMode] = useState("list"); // "list" | "grid"

  // Bulk selection
  const [selected, setSelected] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [deletingId, setDeletingId] = useState("");

  const searchTimer = useRef(null);

  // Load industries
  useEffect(() => {
    let ignore = false;
    async function loadIndustries() {
      try {
        const [indRes, prodRes] = await Promise.all([
          request("/api/public/industries", { method: "GET" }).catch(() => request("/api/industries", { method: "GET" })),
          getMyShopProducts().catch(() => []),
        ]);
        const list = normIndustries(indRes);
        const globalIndustries = (Array.isArray(list) ? list : [])
          .map((i) => ({ id: normLower(getIndustryKey(i)), name: normStr(i.name || i.label || ""), categories: Array.isArray(i.categories) ? i.categories : [] }))
          .filter((x) => x.id && x.name);

        const shopProducts = Array.isArray(prodRes) ? prodRes : (prodRes.items || prodRes.products || []);
        const usedInd = new Set(); const usedCat = new Set();
        shopProducts.forEach((p) => {
          const iId = normLower(p.industryId || p.industry?._id || p.industry || "");
          const cId = normLower(p.categoryId || p.category?._id || p.category || "");
          if (iId) usedInd.add(iId);
          if (cId) usedCat.add(cId);
        });

        const filtered = globalIndustries
          .filter((ind) => usedInd.has(ind.id))
          .map((ind) => ({ ...ind, categories: ind.categories.filter((cat) => usedCat.has(normLower(getCategoryKey(cat)))) }))
          .sort((a, b) => a.name.localeCompare(b.name));

        if (!ignore) setIndustries(filtered);
      } catch {}
    }
    loadIndustries();
    return () => { ignore = true; };
  }, []);

  // Build categories
  useEffect(() => {
    if (!industryId) { setCategories([]); setCategoryId(""); return; }
    const ind = industries.find((x) => x.id === industryId);
    const cats = (ind?.categories || [])
      .filter((c) => c && c.active !== false)
      .map((c) => ({ id: normLower(getCategoryKey(c)), label: normStr(c.label || c.name || "") }))
      .filter((c) => c.id && c.label)
      .sort((a, b) => a.label.localeCompare(b.label));
    setCategories(cats);
    setCategoryId("");
  }, [industryId, industries]);

  // Load products
  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (industryId) params.set("industryId", industryId);
      if (categoryId) params.set("categoryId", categoryId);
      if (statusFilter === "in_stock") params.set("inStock", "true");
      if (statusFilter === "out_of_stock") params.set("inStock", "false");
      if (sort) params.set("sort", sort);
      params.set("limit", "200");

      const data = await request(`/api/shop/products?${params.toString()}`, { method: "GET" });
      setProducts(normalizeProductsResponse(data));
      const f = await fetchFeaturedIdsBestEffort();
      setFeaturedIds(f);
    } catch (e) {
      setErr(e?.message || "Failed to load products");
      setProducts([]);
    } finally { setLoading(false); }
  }, [q, industryId, categoryId, statusFilter, sort]);

  useEffect(() => { load(); }, []); // eslint-disable-line

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(load, q ? 280 : 0);
    return () => { if (searchTimer.current) clearTimeout(searchTimer.current); };
  }, [q, industryId, categoryId, statusFilter, sort, load]);

  // Featured products
  const featuredSet = useMemo(() => new Set((featuredIds || []).map(String)), [featuredIds]);

  // Filter by featured locally
  const filteredProducts = useMemo(() => {
    if (statusFilter === "featured") return products.filter((p) => featuredSet.has(String(pickId(p))));
    return products;
  }, [products, statusFilter, featuredSet]);

  // Delete
  async function onDelete(id) {
    if (!id || !window.confirm("Delete this product?")) return;
    setDeletingId(id); setErr("");
    try {
      await deleteShopProduct(id);
      setProducts((prev) => prev.filter((p) => String(pickId(p)) !== String(id)));
      if (featuredIds.includes(String(id))) {
        const next = featuredIds.filter((x) => x !== String(id));
        setFeaturedIds(next);
        await saveFeaturedIdsBestEffort(next).catch(() => {});
      }
    } catch (e) { setErr(e?.message || "Failed to delete"); }
    finally { setDeletingId(""); }
  }

  // Save a single-variant patch by sending the full variants array.
  // Backend preserves variant _ids via parseVariantsPayload.
  async function saveProductVariants(productId, nextVariants) {
    const body = JSON.stringify({ variants: nextVariants });
    const res = await request(`/api/shop/products/${encodeURIComponent(productId)}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body,
    });
    const updated = res?.product || res?.data || res;
    if (updated && (updated._id || updated.id)) {
      setProducts((prev) => prev.map((p) => String(pickId(p)) === String(productId) ? { ...p, ...updated } : p));
    }
  }

  async function onVariantUpdate(productId, variantIndex, patch) {
    const product = products.find((p) => String(pickId(p)) === String(productId));
    if (!product) return;
    const current = Array.isArray(product.variants) ? product.variants : [];
    const nextVariants = current.map((v, i) => i === variantIndex ? { ...v, ...patch } : v);
    try {
      await saveProductVariants(productId, nextVariants);
    } catch (e) {
      setErr(e?.message || "Failed to update variant");
    }
  }

  async function onVariantRemove(productId, variantIndex) {
    const product = products.find((p) => String(pickId(p)) === String(productId));
    if (!product) return;
    const current = Array.isArray(product.variants) ? product.variants : [];
    const nextVariants = current.filter((_, i) => i !== variantIndex);
    try {
      await saveProductVariants(productId, nextVariants);
    } catch (e) {
      setErr(e?.message || "Failed to remove variant");
    }
  }

  // Save the shop's chosen mall grouping option for this product
  async function onGroupingChange(productId, newGrouping) {
    try {
      const body = JSON.stringify({ groupingOption: newGrouping || "" });
      const res = await request(`/api/shop/products/${encodeURIComponent(productId)}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body,
      });
      const updated = res?.product || res?.data || res;
      if (updated && (updated._id || updated.id)) {
        setProducts((prev) => prev.map((p) =>
          String(pickId(p)) === String(productId) ? { ...p, ...updated } : p
        ));
      } else {
        // Backend didn't return product — update locally
        setProducts((prev) => prev.map((p) =>
          String(pickId(p)) === String(productId) ? { ...p, groupingOption: newGrouping || "" } : p
        ));
      }
    } catch (e) {
      setErr(e?.message || "Failed to update grouping");
    }
  }

  // Toggle featured
  async function toggleFeatured(productId) {
    const id = String(productId || "").trim();
    if (!id) return;
    const exists = featuredIds.includes(id);
    const next = exists ? featuredIds.filter((x) => x !== id) : [...featuredIds, id];
    setFeaturedIds(next);
    setSavingFeatured(true);
    try { await saveFeaturedIdsBestEffort(next); } catch (e) { setFeaturedIds(featuredIds); setErr(e?.message || "Failed"); }
    finally { setSavingFeatured(false); }
  }

  // Bulk
  const toggleSelect = (id) => {
    setSelected((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  };
  const selectAll = () => {
    if (selected.size === filteredProducts.length) setSelected(new Set());
    else setSelected(new Set(filteredProducts.map((p) => pickId(p))));
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selected);
    if (!ids.length || !window.confirm(`Delete ${ids.length} product(s)?`)) return;
    setBulkLoading(true);
    let ok = 0;
    for (const id of ids) {
      try { await deleteShopProduct(id); setProducts((prev) => prev.filter((p) => pickId(p) !== id)); ok++; } catch {}
    }
    setBulkLoading(false); setSelected(new Set());
    if (ok) alert(`Deleted ${ok}/${ids.length} products`);
  };

  const handleBulkFeature = async (on) => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    const next = on ? [...new Set([...featuredIds, ...ids])] : featuredIds.filter((x) => !ids.includes(x));
    setFeaturedIds(next); setSavingFeatured(true);
    try { await saveFeaturedIdsBestEffort(next); } catch { setFeaturedIds(featuredIds); }
    finally { setSavingFeatured(false); setSelected(new Set()); }
  };

  const statusPills = [
    { key: "", label: "All" },
    { key: "in_stock", label: "In Stock" },
    { key: "out_of_stock", label: "Out of Stock" },
    { key: "featured", label: "⭐ Featured" },
  ];

  return (
    <div className="mp-page page-responsive">
      {/* Header */}
      <div className="mp-header">
        <div>
          <h1 className="mp-title">Products</h1>
          <p className="mp-subtitle">{loading ? "Loading..." : `${filteredProducts.length} product${filteredProducts.length === 1 ? "" : "s"}`}{savingFeatured ? " · Saving ⭐..." : ""}</p>
        </div>
        <div className="mp-header-actions">
          <button className="mp-btn mp-btn-primary" onClick={() => nav("/shop/add-product")}>+ Add Product</button>
          <button className="mp-btn mp-btn-ghost" onClick={load} disabled={loading}>{loading ? "..." : "↻"}</button>
          <div className="mp-view-toggle">
            <button className={`mp-view-btn ${viewMode === "list" ? "mp-view-active" : ""}`} onClick={() => setViewMode("list")} title="List view">≡</button>
            <button className={`mp-view-btn ${viewMode === "grid" ? "mp-view-active" : ""}`} onClick={() => setViewMode("grid")} title="Grid view">⊞</button>
          </div>
        </div>
      </div>

      {err && <div className="mp-error">⚠️ {err}</div>}

      {/* Filters */}
      <div className="mp-filters-bar">
        <input className="mp-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="🔍 Search products..." />
        <select className="mp-select" value={industryId} onChange={(e) => setIndustryId(e.target.value)}>
          <option value="">All Industries</option>
          {industries.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
        </select>
        <select className="mp-select" value={categoryId} onChange={(e) => setCategoryId(e.target.value)} disabled={!industryId}>
          <option value="">{industryId ? "All Categories" : "Pick industry first"}</option>
          {categories.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
        <select className="mp-select mp-select-sort" value={sort} onChange={(e) => setSort(e.target.value)}>
          <option value="newest">Newest</option>
          <option value="views">Most Viewed</option>
          <option value="priceasc">Price ↑</option>
          <option value="pricedesc">Price ↓</option>
        </select>
      </div>

      {/* Status pills */}
      <div className="mp-pills">
        {statusPills.map((s) => (
          <button key={s.key} className={`mp-pill ${statusFilter === s.key ? "mp-pill-active" : ""}`} onClick={() => setStatusFilter(s.key)}>{s.label}</button>
        ))}
        {(q || industryId || categoryId || statusFilter) && (
          <button className="mp-pill mp-pill-clear" onClick={() => { setQ(""); setIndustryId(""); setCategoryId(""); setStatusFilter(""); setSort("newest"); }}>✕ Clear</button>
        )}
      </div>

      {/* Bulk Action Bar */}
      {selected.size > 0 && (
        <div className="mp-bulk-bar">
          <div className="mp-bulk-info">
            <input type="checkbox" checked={selected.size === filteredProducts.length} onChange={selectAll} />
            <span>{selected.size} selected</span>
          </div>
          <div className="mp-bulk-actions">
            {bulkLoading ? <span>Processing...</span> : (
              <>
                <button className="mp-btn mp-btn-sm" onClick={() => handleBulkFeature(true)}>⭐ Feature</button>
                <button className="mp-btn mp-btn-sm" onClick={() => handleBulkFeature(false)}>☆ Unfeature</button>
                <button className="mp-btn mp-btn-sm mp-btn-danger-sm" onClick={handleBulkDelete}>🗑 Delete</button>
                <button className="mp-btn mp-btn-sm" onClick={() => setSelected(new Set())}>✕</button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Content */}
      {loading && filteredProducts.length === 0 ? (
        <div className="mp-empty">Loading products...</div>
      ) : filteredProducts.length === 0 ? (
        <div className="mp-empty">
          <div className="mp-empty-icon">📦</div>
          <div className="mp-empty-text">No products found</div>
          <div className="mp-empty-sub">Try clearing filters or add your first product</div>
          <button className="mp-btn mp-btn-primary" style={{ marginTop: 16 }} onClick={() => nav("/shop/add-product")}>+ Add Product</button>
        </div>
      ) : viewMode === "list" ? (
        <ProductTable
          products={filteredProducts}
          featuredSet={featuredSet}
          selected={selected}
          toggleSelect={toggleSelect}
          selectAll={selectAll}
          onEdit={(id) => nav(`/shop/dashboard/edit-product/${id}`)}
          onDelete={onDelete}
          onToggleFeatured={toggleFeatured}
          deletingId={deletingId}
          onVariantUpdate={onVariantUpdate}
          onVariantRemove={onVariantRemove}
          onGroupingChange={onGroupingChange}
        />
      ) : (
        <ProductGrid
          products={filteredProducts}
          featuredSet={featuredSet}
          selected={selected}
          toggleSelect={toggleSelect}
          onEdit={(id) => nav(`/shop/dashboard/edit-product/${id}`)}
          onDelete={onDelete}
          onToggleFeatured={toggleFeatured}
          deletingId={deletingId}
        />
      )}
    </div>
  );
}

/* =========================================================
   VARIANT ROWS — inline editable list for each product
   ========================================================= */
function variantLabel(v) {
  if (!v) return "";
  const ovs = Array.isArray(v.optionValues) ? v.optionValues : [];
  const parts = ovs.map((ov) => String(ov?.value || "").trim()).filter(Boolean);
  return parts.join(" / ");
}

// Lets the shop choose which option name groups cards in the mall.
// Auto-detected: prefers "color/colour", else first non-size option.
// Override: shop picks any of their option names from a dropdown.
function GroupingOptionRow({ product, productId, saving, onChange }) {
  const SIZE_RX = /^(size|sz|weight|wt)$/i;
  const allOptions = Array.isArray(product?.options) ? product.options : [];
  // Build the option-name list:
  // - Prefer canonical options array
  // - Fall back to scanning variant optionValues if no options array
  let optionNames = allOptions
    .map((o) => String(o?.name || "").trim())
    .filter(Boolean);
  if (optionNames.length === 0 && Array.isArray(product?.variants)) {
    const set = new Set();
    for (const v of product.variants) {
      for (const ov of (v.optionValues || [])) {
        const n = String(ov?.name || "").trim();
        if (n) set.add(n);
      }
    }
    optionNames = [...set];
  }
  // Non-size options are valid grouping choices
  const nonSizeOptions = optionNames.filter((n) => !SIZE_RX.test(n));
  if (nonSizeOptions.length === 0) return null; // only sizes → no grouping choice to make

  const current = String(product?.groupingOption || "").trim();
  return (
    <tr className="mp-grouping-row" style={{ background: "rgba(139,92,246,0.06)" }}>
      <td className="mp-td" />
      <td className="mp-td" colSpan={6} style={{ paddingLeft: 28 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", flexWrap: "wrap" }}>
          <span style={{ fontSize: 11, color: "#94a3b8", letterSpacing: "0.05em", textTransform: "uppercase" }}>
            Group mall cards by
          </span>
          <select
            disabled={saving}
            value={current}
            onChange={(e) => onChange(e.target.value)}
            style={{
              padding: "5px 10px", borderRadius: 6,
              border: "1px solid rgba(255,255,255,0.15)",
              background: "rgba(255,255,255,0.04)", color: "#e2e8f0",
              fontSize: 12, outline: "none", minWidth: 160,
            }}
          >
            <option value="">Auto (prefers Color / first non-size)</option>
            {nonSizeOptions.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
          <span style={{ fontSize: 11, color: "#64748b" }}>
            {current
              ? `Cards will split per "${current}" value`
              : "Will use Color if present, else the first non-size option"}
          </span>
        </div>
      </td>
    </tr>
  );
}

function VariantRow({ v, currency, onChange, onRemove, saving }) {
  const [stock, setStock] = useState(num(v?.stock, 0));
  const [price, setPrice] = useState(v?.price == null ? "" : String(v.price));
  const [active, setActive] = useState(v?.isActive !== false);
  const dirty = useRef(false);

  useEffect(() => {
    if (!dirty.current) return;
    const t = setTimeout(() => {
      onChange({
        stock: Math.max(0, Math.floor(num(stock, 0))),
        price: price === "" ? null : Math.max(0, num(price, 0)),
        isActive: !!active,
      });
      dirty.current = false;
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stock, price, active]);

  const bump = (setter, val) => { dirty.current = true; setter(val); };
  const img = Array.isArray(v?.images) && v.images[0]
    ? absUrl(typeof v.images[0] === "string" ? v.images[0] : (v.images[0]?.url || v.images[0]?.path || ""))
    : "";

  return (
    <tr className="mp-variant-row" style={{ background: active ? "transparent" : "rgba(255,255,255,0.02)", opacity: active ? 1 : 0.55 }}>
      <td className="mp-td" />
      <td className="mp-td" style={{ paddingLeft: 28 }}>
        <div className="mp-thumb mp-thumb-sm">
          {img ? <img src={img} alt="" onError={(e) => { e.target.onerror = null; e.target.style.display = "none"; }} /> : <span>📷</span>}
        </div>
      </td>
      <td className="mp-td" colSpan={2}>
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <span style={{ fontWeight: 600, color: "#e2e8f0" }}>{variantLabel(v) || "—"}</span>
          {v?.sku && <span style={{ fontSize: 11, color: "#94a3b8" }}>SKU: {v.sku}</span>}
        </div>
      </td>
      <td className="mp-td">
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <span style={{ fontSize: 11, color: "#64748b" }}>{currency}</span>
          <input
            type="number" min="0" step="0.01" value={price}
            placeholder="base" disabled={saving}
            onChange={(e) => bump(setPrice, e.target.value)}
            style={inputStyle(80)}
          />
        </div>
      </td>
      <td className="mp-td">
        <input
          type="number" min="0" step="1" value={stock} disabled={saving}
          onChange={(e) => bump(setStock, e.target.value)}
          style={inputStyle(70)}
        />
      </td>
      <td className="mp-td">
        <span style={{ fontSize: 10, color: "#475569", fontStyle: "italic" }}>via Sidebar</span>
      </td>
      <td className="mp-td" onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", alignItems: "center" }}>
          <button
            type="button"
            onClick={() => bump(setActive, !active)}
            disabled={saving}
            title={active ? "Deactivate variant" : "Activate variant"}
            style={pillStyle(active ? "#10b981" : "#475569")}
          >
            {active ? "Active" : "Hidden"}
          </button>
          <button
            type="button"
            onClick={() => { if (window.confirm(`Remove variant "${variantLabel(v) || "—"}"?`)) onRemove(); }}
            disabled={saving}
            title="Remove variant"
            style={pillStyle("#ef4444")}
          >
            ✕
          </button>
        </div>
      </td>
    </tr>
  );
}

function inputStyle(w) {
  return {
    width: w, padding: "5px 8px", borderRadius: 6,
    border: "1px solid rgba(255,255,255,0.1)",
    background: "rgba(255,255,255,0.04)", color: "#e2e8f0",
    fontSize: 12, outline: "none",
  };
}
function pillStyle(bg) {
  return {
    background: bg, color: "#fff", border: "none",
    padding: "4px 10px", borderRadius: 999, fontSize: 11,
    fontWeight: 600, cursor: "pointer",
  };
}

/* =========================================================
   TABLE VIEW (Shopify-style)
   ========================================================= */
function ProductTable({ products, featuredSet, selected, toggleSelect, selectAll, onEdit, onDelete, onToggleFeatured, deletingId, onVariantUpdate, onVariantRemove, onGroupingChange }) {
  const [expandedId, setExpandedId] = useState("");
  const [savingProductId, setSavingProductId] = useState("");

  return (
    <div className="mp-table-wrap">
      <table className="mp-table">
        <thead>
          <tr>
            <th className="mp-th mp-th-check">
              <input type="checkbox" checked={selected.size === products.length && products.length > 0} onChange={selectAll} />
            </th>
            <th className="mp-th mp-th-img"></th>
            <th className="mp-th mp-th-title">Product</th>
            <th className="mp-th mp-th-status">Status</th>
            <th className="mp-th mp-th-price">Price</th>
            <th className="mp-th mp-th-stock">Inventory</th>
            <th className="mp-th mp-th-category">Category</th>
            <th className="mp-th mp-th-actions"></th>
          </tr>
        </thead>
        <tbody>
          {products.map((p) => {
            const id = pickId(p);
            const img = pickImage(p);
            const variantList = Array.isArray(p?.variants) ? p.variants : [];
            const variantPrices = variantList.map((v) => num(v?.price, NaN)).filter(Number.isFinite);
            const basePrice = num(p.localPrice ?? p.price ?? 0, 0);
            const displayPrice = variantPrices.length > 0 ? Math.min(...variantPrices) : basePrice;
            const hasPriceRange = variantPrices.length > 0 && variantPrices.some((v) => v !== displayPrice);
            const currency = String(p.currency || "USD").toUpperCase();

            const variantStockTotal = variantList.reduce((sum, v) => sum + Math.max(0, num(v?.stock, 0)), 0);
            const qty = variantList.length > 0 ? variantStockTotal : num(p.quantity || 0, 0);
            const inStock = variantList.length > 0 ? variantStockTotal > 0 : Boolean(p.inStock);
            const isFeatured = featuredSet.has(String(id));

            const catName = normStr(p.categoryLabel) || normStr(p.categoryId) || normStr(p.category) || "-";
            const views = num(p.views || 0, 0);

            const hasVariants = variantList.length > 0;
            const isExpanded = expandedId === id;
            const isSaving = savingProductId === id;

            return (
              <React.Fragment key={id}>
                <tr className={`mp-row ${selected.has(id) ? "mp-row-selected" : ""}`}>
                  <td className="mp-td mp-td-check" onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" checked={selected.has(id)} onChange={() => toggleSelect(id)} />
                  </td>
                  <td className="mp-td mp-td-img" onClick={() => hasVariants ? setExpandedId(isExpanded ? "" : id) : onEdit(id)} style={{ cursor: "pointer" }}>
                    <div className="mp-thumb">
                      {img ? <img src={img} alt="" onError={(e) => { e.target.onerror = null; e.target.style.display = "none"; }} /> : <span>📷</span>}
                    </div>
                  </td>
                  <td className="mp-td mp-td-title" onClick={() => hasVariants ? setExpandedId(isExpanded ? "" : id) : onEdit(id)} style={{ cursor: "pointer" }}>
                    <div className="mp-product-name" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {hasVariants && (
                        <span style={{ display: "inline-block", transition: "transform 0.15s", transform: isExpanded ? "rotate(90deg)" : "rotate(0)", color: "#64748b", fontSize: 11 }}>▶</span>
                      )}
                      {p.title || "Untitled"}
                    </div>
                    {hasVariants && (
                      <div className="mp-product-variants" style={{ color: isExpanded ? "#a78bfa" : undefined, fontWeight: isExpanded ? 600 : undefined }}>
                        {isExpanded ? "Hide variants" : `${variantList.length} variant${variantList.length > 1 ? "s" : ""} (click to manage)`}
                      </div>
                    )}
                  </td>
                  <td className="mp-td mp-td-status" onClick={() => onEdit(id)} style={{ cursor: "pointer" }}>
                    <div className="mp-status-row">
                      <span className={`mp-dot ${p.isActive === false ? "mp-dot-red" : inStock ? "mp-dot-green" : "mp-dot-red"}`} />
                      <span className="mp-status-text">{p.isActive === false ? "Hidden (draft)" : inStock ? "Active" : "Out of Stock"}</span>
                    </div>
                    {isFeatured && <span className="mp-featured-tag">⭐ Featured</span>}
                  </td>
                  <td className="mp-td mp-td-price" onClick={() => onEdit(id)} style={{ cursor: "pointer" }}>
                    <span className="mp-price">{hasPriceRange ? `From ` : ""}{currency} {displayPrice.toFixed(2)}</span>
                  </td>
                  <td className="mp-td mp-td-stock" onClick={() => onEdit(id)} style={{ cursor: "pointer" }}>
                    <span className={`mp-stock ${qty === 0 ? "mp-stock-zero" : ""}`}>{qty} in stock</span>
                  </td>
                  <td className="mp-td mp-td-category" onClick={() => onEdit(id)} style={{ cursor: "pointer" }}>
                    <span className="mp-cat-text">{catName}</span>
                  </td>
                  <td className="mp-td mp-td-actions" onClick={(e) => e.stopPropagation()}>
                    <ProductMenu
                      id={id}
                      isFeatured={isFeatured}
                      onEdit={onEdit}
                      onDelete={onDelete}
                      onToggleFeatured={onToggleFeatured}
                      deleting={deletingId === id}
                      views={views}
                    />
                  </td>
                </tr>

                {/* Expanded variant editor — Shopify-style inline */}
                {isExpanded && hasVariants && (
                  <>
                    {/* Grouping-option override — lets the shop pick which option
                        groups the cards in the mall (e.g. Material instead of Color) */}
                    <GroupingOptionRow
                      product={p}
                      productId={id}
                      saving={isSaving}
                      onChange={async (newGrouping) => {
                        setSavingProductId(id);
                        await onGroupingChange(id, newGrouping);
                        setSavingProductId("");
                      }}
                    />
                    <tr className="mp-variant-header" style={{ background: "rgba(99,102,241,0.08)" }}>
                      <td className="mp-td" />
                      <td className="mp-td" style={{ paddingLeft: 28, fontSize: 11, color: "#94a3b8", letterSpacing: "0.05em" }}>VARIANT</td>
                      <td className="mp-td" colSpan={2} />
                      <td className="mp-td" style={{ fontSize: 11, color: "#94a3b8" }}>PRICE</td>
                      <td className="mp-td" style={{ fontSize: 11, color: "#94a3b8" }}>STOCK</td>
                      <td className="mp-td" style={{ fontSize: 11, color: "#94a3b8" }}>DISCOUNT</td>
                      <td className="mp-td" style={{ fontSize: 11, color: "#94a3b8", textAlign: "right" }}>STATUS / REMOVE</td>
                      {/* Discounts are managed in the Sidebar tab → set via sale categories */}
                    </tr>
                    {variantList.map((v, i) => (
                      <VariantRow
                        key={String(v?._id || `idx-${i}`)}
                        v={v}
                        currency={currency}
                        saving={isSaving}
                        onChange={async (patch) => {
                          setSavingProductId(id);
                          await onVariantUpdate(id, i, patch);
                          setSavingProductId("");
                        }}
                        onRemove={async () => {
                          setSavingProductId(id);
                          await onVariantRemove(id, i);
                          setSavingProductId("");
                        }}
                      />
                    ))}
                  </>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* =========================================================
   GRID VIEW (visual card layout)
   ========================================================= */
function ProductGrid({ products, featuredSet, selected, toggleSelect, onEdit, onDelete, onToggleFeatured, deletingId }) {
  return (
    <div className="mp-grid">
      {products.map((p) => {
        const id = pickId(p);
        const img = pickImage(p);
        const variantList = Array.isArray(p?.variants) ? p.variants : [];
        const variantPrices = variantList.map((v) => num(v?.price, NaN)).filter(Number.isFinite);
        const basePrice = num(p.localPrice ?? p.price ?? 0, 0);
        const displayPrice = variantPrices.length > 0 ? Math.min(...variantPrices) : basePrice;
        const currency = String(p.currency || "USD").toUpperCase();

        const variantStockTotal = variantList.reduce((sum, v) => sum + Math.max(0, num(v?.stock, 0)), 0);
        const qty = variantList.length > 0 ? variantStockTotal : num(p.quantity || 0, 0);
        const inStock = variantList.length > 0 ? variantStockTotal > 0 : Boolean(p.inStock);
        const isFeatured = featuredSet.has(String(id));

        return (
          <div key={id} className={`mp-gcard ${selected.has(id) ? "mp-gcard-selected" : ""}`} onClick={() => onEdit(id)}>
            <div className="mp-gcard-check" onClick={(e) => e.stopPropagation()}>
              <input type="checkbox" checked={selected.has(id)} onChange={() => toggleSelect(id)} />
            </div>
            <div className="mp-gcard-img">
              {img ? <img src={img} alt="" onError={(e) => { e.target.onerror = null; e.target.src = ""; }} /> : <div className="mp-gcard-noimg">📷</div>}
              {isFeatured && <span className="mp-gcard-star">⭐</span>}
              <span className={`mp-gcard-dot ${inStock ? "mp-dot-green" : "mp-dot-red"}`} />
            </div>
            <div className="mp-gcard-body">
              <div className="mp-gcard-title">{p.title || "Untitled"}</div>
              <div className="mp-gcard-price">{currency} {displayPrice.toFixed(2)}</div>
              <div className="mp-gcard-stock">{p.isActive === false ? "Hidden (draft) · not visible to buyers" : `${qty} in stock`}</div>
            </div>
            <div className="mp-gcard-actions" onClick={(e) => e.stopPropagation()}>
              <ProductMenu id={id} isFeatured={isFeatured} onEdit={onEdit} onDelete={onDelete} onToggleFeatured={onToggleFeatured} deleting={deletingId === id} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* =========================================================
   PRODUCT MENU (⋮ dropdown)
   ========================================================= */
function ProductMenu({ id, isFeatured, onEdit, onDelete, onToggleFeatured, deleting, views }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div className="mp-menu-wrap" ref={ref}>
      <button className="mp-menu-trigger" onClick={() => setOpen(!open)}>⋮</button>
      {open && (
        <div className="mp-menu-dropdown">
          <button className="mp-menu-item" onClick={() => { onEdit(id); setOpen(false); }}>✏️ Edit</button>
          <button className="mp-menu-item" onClick={() => { onToggleFeatured(id); setOpen(false); }}>
            {isFeatured ? "☆ Remove Featured" : "⭐ Add to Featured"}
          </button>
          {views !== undefined && <div className="mp-menu-info">👁 {views} views</div>}
          <hr className="mp-menu-divider" />
          <button className="mp-menu-item mp-menu-danger" onClick={() => { onDelete(id); setOpen(false); }} disabled={deleting}>
            {deleting ? "Deleting..." : "🗑 Delete"}
          </button>
        </div>
      )}
    </div>
  );
}
